import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { WelcomePage } from '@/features/onboarding/WelcomePage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { PartiesPage } from '@/features/parties/PartiesPage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { PosPage } from '@/features/pos/PosPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { CashSessionsPage } from '@/features/cash-sessions/CashSessionsPage';
import { ReceivablesPage } from '@/features/receivables/ReceivablesPage';
import { InventoryPage } from '@/features/inventory/InventoryPage';
import { PurchasesPage } from '@/features/purchases/PurchasesPage';
import { PayablesPage } from '@/features/purchases/PayablesPage';
import { TreasuryPage } from '@/features/treasury/TreasuryPage';
import { ExpensesPage } from '@/features/expenses/ExpensesPage';
import { AppShell } from '@/shared/layout/AppShell';
import { RequireAuth } from '@/shared/layout/RequireAuth';
import { RequireModule } from '@/shared/layout/RequireModule';
import { ConfirmProvider } from '@/shared/modals/ConfirmDialog';
import { useAuthStore } from '@/stores/auth.store';

// La landing pública va en su propio chunk: no pesa en el bundle de la app.
const LandingPage = lazy(() =>
  import('@/features/landing/LandingPage').then((m) => ({ default: m.LandingPage })),
);

/** `/` sin sesión = landing pública; con sesión (o cualquier otra ruta) = la app. */
function Home(): JSX.Element {
  const { pathname } = useLocation();
  const hasSession = useAuthStore((s) => !!s.accessToken);
  if (pathname === '/' && !hasSession) {
    return (
      <Suspense fallback={null}>
        <LandingPage />
      </Suspense>
    );
  }
  return (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  );
}

export function App(): JSX.Element {
  return (
    <ConfirmProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/bienvenida"
          element={
            <RequireAuth>
              <WelcomePage />
            </RequireAuth>
          }
        />
        <Route path="/" element={<Home />}>
          <Route index element={<DashboardPage />} />
          <Route path="pos" element={<PosPage />} />
          <Route path="cash-sessions" element={<CashSessionsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="receivables" element={<ReceivablesPage />} />
          <Route path="parties" element={<PartiesPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route
            path="inventory"
            element={
              <RequireModule moduleKey="inventory">
                <InventoryPage />
              </RequireModule>
            }
          />
          <Route path="purchases" element={<PurchasesPage />} />
          <Route path="payables" element={<PayablesPage />} />
          <Route path="treasury" element={<TreasuryPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ConfirmProvider>
  );
}
